// The Code Tool's editor modal. Opens when the Code tool is active
// and the user clicks a `cpu` machine (the chip or its RAM / screen).
// It shows the program living inside that CPU as disassembled
// mnemonics, lets the user edit assembly, and loads it back —
// restarting the CPU from PC=0. This is the "click the screen and
// see our ISA" panel.

import { useEffect, useState } from "react";

import type { SandboxLike } from "../sim/types";

export interface CodePanelProps {
  sandbox: SandboxLike | null;
  /** Grid cell the user clicked. Null when the panel is closed. */
  target: { x: number; y: number } | null;
  onClose: () => void;
}

export function CodePanel({ sandbox, target, onClose }: CodePanelProps) {
  // Disassembly read straight from the CPU (read-only reference) and
  // the editable assembly buffer the user loads back. We seed the
  // buffer from the disassembly so editing starts from what's there.
  const [disasm, setDisasm] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState<string | null>(null);

  // Re-read the program whenever the target cell changes (a new click
  // while the panel is already open re-targets it).
  useEffect(() => {
    if (!sandbox || !target || !sandbox.codeAt) {
      setDisasm("");
      setSource("");
      setStatus(null);
      return;
    }
    const text = sandbox.codeAt(target.x, target.y);
    setDisasm(text);
    // Strip the `addr:` prefixes so the editable buffer is plain
    // assembly the assembler will accept back.
    const asm = text
      .split("\n")
      .map((line) => line.replace(/^\s*\d+:\s*/, ""))
      .join("\n");
    setSource(asm);
    setStatus(null);
  }, [sandbox, target]);

  if (!target) return null;

  const load = () => {
    if (!sandbox || !sandbox.codeLoad) {
      setStatus("this sandbox can't load code (no wasm engine)");
      return;
    }
    const err = sandbox.codeLoad(target.x, target.y, source);
    if (err === "") {
      setStatus("loaded — CPU restarted from PC=0");
      // Refresh the disassembly to reflect what actually landed.
      if (sandbox.codeAt) setDisasm(sandbox.codeAt(target.x, target.y));
    } else {
      setStatus(`assemble error: ${err}`);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal code-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="code-panel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="code-panel-title">
          Code Tool — CPU @ ({target.x}, {target.y})
        </h2>
        <p className="code-panel-hint">
          The program inside this machine. Edit the assembly and Load it
          to reprogram the CPU (it restarts from the top). See ASSEMBLY.md
          for the instruction set.
        </p>
        <div className="code-panel-cols">
          <div className="code-panel-col">
            <h3>Disassembly</h3>
            <pre className="code-panel-disasm">{disasm || "(no code / empty memory)"}</pre>
          </div>
          <div className="code-panel-col">
            <h3>Assembly (editable)</h3>
            <textarea
              className="formula-input code-panel-editor"
              rows={16}
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                setStatus(null);
              }}
              spellCheck={false}
            />
          </div>
        </div>
        <div className="panel-actions">
          <button type="button" className="formula-run" onClick={load} disabled={!sandbox}>
            Load &amp; restart CPU
          </button>
          <button type="button" className="formula-secondary" onClick={onClose}>
            Close
          </button>
        </div>
        {status && <div className="formula-status">{status}</div>}
      </div>
    </div>
  );
}
